'use client'

import { forwardRef, useId } from 'react'

import { cn } from '@/core/utils/common'

import { fieldStyles } from './field-styles'

import type { TextareaHTMLAttributes } from 'react'

/**
 * bare = 테두리 · 여백 없음 — 입력창 묶음(채팅 입력 · 프롬프트 칸)처럼 바깥 상자가 테두리와 focus-within 을 맡을 때. 글자 · 크기 기본값은 유지한다.
 * unstyled = 기본 · 변형 클래스 없이 호출부 className 만 쓴다(InputText 의 bare 와 같은 뜻). label · helperText · error 가 없으면 감싸는 div 도 두지 않는다.
 */
type TextareaVariant = 'outline' | 'underline' | 'bare' | 'unstyled'

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string
  error?: string
  helperText?: string
  variant?: TextareaVariant
  classNames?: {
    container?: string
    textarea?: string
    label?: string
    error?: string
  }
}

const variantStyles: Record<TextareaVariant, string> = {
  // 면 · 테두리는 한 줄 입력과 같은 값(field-styles) — 칸마다 색이 달라지지 않게
  outline: cn(fieldStyles.surface, 'px-4 py-3'),
  underline:
    'border-b border-border bg-transparent px-1 py-3 focus:border-point',
  bare: 'min-h-0! bg-transparent p-0',
  unstyled: '',
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      variant = 'outline',
      id,
      className,
      classNames,
      ...props
    },
    ref
  ) => {
    const generatedId = useId()
    const textareaId = id || props.name || generatedId
    const helperTextId = helperText ? `${textareaId}-helper` : undefined
    const errorId = error ? `${textareaId}-error` : undefined

    const control = (
      <textarea
        ref={ref}
        id={textareaId}
        className={cn(
          // 크기 조절 손잡이는 두지 않는다(2026-10-13 사용자 지정) — 필요한 곳만 classNames.textarea 로 resize-y 를 켠다
          variant !== 'unstyled' &&
            'min-h-28 w-full resize-none text-c15 text-foreground outline-none',
          variant !== 'unstyled' &&
            'transition-[color,background-color,border-color] duration-200',
          variant !== 'unstyled' &&
            'placeholder:text-description disabled:cursor-not-allowed disabled:opacity-50',
          variantStyles[variant],
          error && 'border-error focus:border-error',
          classNames?.textarea,
          className
        )}
        aria-invalid={Boolean(error)}
        aria-describedby={errorId ?? helperTextId}
        {...props}
      />
    )

    if (variant === 'unstyled' && !label && !helperText && !error)
      return control

    return (
      <div className={cn(classNames?.container)}>
        {label && (
          <label
            htmlFor={textareaId}
            className={cn(
              'mb-2 block text-c14 font-medium text-foreground',
              classNames?.label
            )}
          >
            {label}
          </label>
        )}
        {control}
        {helperText && !error && (
          <p
            id={helperTextId}
            className="mt-2 text-c12 leading-5 text-description"
          >
            {helperText}
          </p>
        )}
        {error && (
          <p
            id={errorId}
            role="alert"
            className={cn(
              'mt-2 text-c12 leading-5 text-error',
              classNames?.error
            )}
          >
            {error}
          </p>
        )}
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'
